import type React from "react";

interface ShareButtonProps {
  onClick: () => void;
  isCollaborating: boolean;
  activeCount?: number;
  disabled?: boolean;
  /** Tooltip shown while disabled. Lets callers distinguish why (ux-rules § 6c). */
  disabledReason?: string;
}

export default function ShareButton({
  onClick,
  isCollaborating,
  activeCount = 0,
  disabled = false,
  disabledReason = "Sharing is not available for this drawing",
}: ShareButtonProps) {
  const label = "Share";
  const showCountBadge = activeCount > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-app-usage-surface="sharing"
      data-testid="share-button"
      style={{
        ...buttonStyle,
        ...(isCollaborating ? activeButtonStyle : null),
        ...(disabled ? disabledButtonStyle : null),
      }}
      title={disabled ? disabledReason : label}
    >
      <span>{label}</span>
      {showCountBadge && <span style={countBadgeStyle}>{activeCount}</span>}
    </button>
  );
}

const buttonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  position: "relative",
  borderRadius: 999,
  border: "1px solid var(--dialog-border-color)",
  background: "var(--exms-toolbar-hover, var(--island-bg-color))",
  color: "var(--text-primary-color)",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
};

const activeButtonStyle: React.CSSProperties = {
  background: "var(--exms-success)",
  borderColor: "var(--exms-success)",
  color: "var(--color-on-accent)",
};

const disabledButtonStyle: React.CSSProperties = {
  opacity: 0.55,
  cursor: "not-allowed",
};

const countBadgeStyle: React.CSSProperties = {
  position: "absolute",
  right: -6,
  bottom: -6,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 16,
  height: 16,
  padding: "0 4px",
  borderRadius: 999,
  background: "var(--exms-success-soft)",
  color: "var(--exms-success-text)",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1,
  boxSizing: "border-box",
};
